import { useI18n } from "@expertloop/i18n/react";
import type { MessageKey } from "@expertloop/i18n";
import type { AnalyticsFunnel } from "@expertloop/shared";

/**
 * Libellé de chaque étape connue.
 *
 * Une étape que le serveur renverrait sans libellé connu est affichée sous son
 * identifiant plutôt que masquée : mieux vaut un intitulé technique visible
 * qu'un trou silencieux dans un parcours.
 */
const STEP_LABELS: Record<string, MessageKey> = {
	requestCreated: "adminOverview.funnelRequestCreated",
	requestAccepted: "adminOverview.funnelRequestAccepted",
	paymentAuthorized: "adminOverview.funnelPaymentAuthorized",
	delivered: "adminOverview.funnelDelivered",
	clientConfirmed: "adminOverview.funnelClientConfirmed",
	captured: "adminOverview.funnelCaptured",
	registered: "adminOverview.funnelRegistered",
	stripeReady: "adminOverview.funnelStripeReady",
	publishedProduct: "adminOverview.funnelPublishedProduct",
	firstRequest: "adminOverview.funnelFirstRequest",
	firstSale: "adminOverview.funnelFirstSale",
	// Invitation d'experts externes (REFERRAL-5)
	invitationIssued: "adminOverview.funnelInvitationIssued",
	invitationViewed: "adminOverview.funnelInvitationViewed",
	invitationAccepted: "adminOverview.funnelInvitationAccepted",
	invitationAccountCreated: "adminOverview.funnelInvitationAccountCreated",
	invitationFundedMission: "adminOverview.funnelInvitationFundedMission",
	invitationFreeMission: "adminOverview.funnelInvitationFreeMission",
	invitationCompletedMission: "adminOverview.funnelInvitationCompletedMission",
	invitationDeclined: "adminOverview.funnelInvitationDeclined",
	invitationRedirected: "adminOverview.funnelInvitationRedirected",
	invitationRedirectOrder: "adminOverview.funnelInvitationRedirectOrder",
	// Parrainage entre experts (lot E6)
	referralAttached: "adminOverview.funnelReferralAttached",
	referralStripeReady: "adminOverview.funnelReferralStripeReady",
	referralPublished: "adminOverview.funnelReferralPublished",
	referralFirstSale: "adminOverview.funnelReferralFirstSale",
	referralSubscribed: "adminOverview.funnelReferralSubscribed",
};

interface FunnelSectionProps {
	title: string;
	funnel: AnalyticsFunnel;
}

/**
 * Un parcours et, sous lui, ce qu'il ne sait pas voir.
 *
 * Les limites ne sont pas un ornement : sans elles, ces nombres se lisent comme
 * un taux de conversion, ce qu'ils ne sont pas.
 */
export function FunnelSection({ title, funnel }: FunnelSectionProps) {
	const { t } = useI18n();
	const widest = Math.max(...funnel.steps.map((step) => step.count), 0);

	return (
		<div className="admin-overview__funnel">
			<h3 className="admin-overview__funnel-title">{title}</h3>
			<ol className="admin-overview__funnel-steps">
				{funnel.steps.map((step) => {
					const labelKey = STEP_LABELS[step.id];
					return (
						<li key={step.id} className="admin-overview__funnel-step">
							<span className="admin-overview__funnel-label">
								{labelKey ? t(labelKey) : step.id}
							</span>
							<span
								className="admin-overview__funnel-track"
								aria-hidden="true"
							>
								<span
									className="admin-overview__funnel-fill"
									style={{ width: widest > 0 ? `${Math.round((step.count / widest) * 100)}%` : "0%" }}
								/>
							</span>
							<span className="admin-overview__funnel-count">{step.count}</span>
						</li>
					);
				})}
			</ol>
			<details className="admin-overview__limitations">
				<summary>{t("adminOverview.funnelLimitations")}</summary>
				<ul>
					{funnel.limitations.map((limitation) => (
						<li key={limitation}>{limitation}</li>
					))}
				</ul>
			</details>
		</div>
	);
}
